import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import List from "@material-ui/core/List";
import ListSubheader from "@material-ui/core/ListSubheader";
import ListItem from "@material-ui/core/ListItem";
import Grid from "@material-ui/core/Grid";
import * as yup from "yup";
import { useFormik } from "formik";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import uuidv4 from "uuid/v4";

import * as constants from "../../../constants";

const MutateIncidentReportSubject = ({
  isOpen,
  onClose,
  onSave,
  name = "",
  sex = null,
  driverLicenseNumber = "",
  driverLicenseState = null,
  ethnicity = null,
  race = null,
  phoneNumber = "",
  address = "",
  description = "",
  type = null,
  height = "",
  weight = "",
  dateOfBirth = "",
  age = "",
  hairColor = null,
  eyeColor = null,
  id = uuidv4(),
}) => {
  const classes = useStyles();

  const [subjectType, setSubjectType] = useState(type);
  const [subjectEthnicity, setSubjectEthnicity] = useState(ethnicity);
  const [subjectRace, setSubjectRace] = useState(race);
  const [subjectSex, setSubjectSex] = useState(sex);
  const [subjectHair, setSubjectHair] = useState(hairColor);
  const [subjectEyes, setSubjectEyes] = useState(eyeColor);
  const [subjectLicenseState, setSubjectLicenseState] = useState(
    driverLicenseState
  );

  const snackbar = useSnackbar();
  const formData = useFormik({
    initialValues: {
      name,
      age,
      height,
      weight,
      dateOfBirth,
      driverLicenseNumber,
      description,
      phoneNumber,
      address,
    },
    onSubmit: (values) => formSubmit(values),
    validationSchema: yup.object({
      name: yup
        .string()
        .max(140, "Must be less than 140 characters")
        .required("Required"),
      age: yup.string().max(140, "Must be less than 140 characters"),
      height: yup.string().max(140, "Must be less than 140 characters"),
      weight: yup.string().max(140, "Must be less than 140 characters"),
      dateOfBirth: yup.string().max(140, "Must be less than 140 characters"),
      description: yup.string().max(2000, "Must be less than 2000 characters"),
      driverLicenseNumber: yup
        .string()
        .max(140, "Must be less than 140 characters"),
      phoneNumber: yup.string().max(140, "Must be less than 140 characters"),
      address: yup.string().max(140, "Must be less than 140 characters"),
    }),
  });

  useEffect(() => {
    if (!formData.isValidating && Object.values(formData.errors).length > 0) {
      snackbar.enqueueSnackbar("Please fix form errors", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [formData.isValidating, formData.errors]);

  const formSubmit = (values) => {
    const subject = {
      name: values.name,
      sex: subjectSex,
      ethnicity: subjectEthnicity,
      race: subjectRace,
      phoneNumber: values.phoneNumber,
      address: values.address,
      driverLicense: {
        number: values.driverLicenseNumber,
        state: subjectLicenseState,
      },
      description: values.description,
      type: subjectType,
      height: values.height,
      weight: values.weight,
      dateOfBirth: values.dateOfBirth,
      age: values.age,
      hairColor: subjectHair,
      eyeColor: subjectEyes,
      id,
    };
    onSave(subject);
  };

  const nameError = formData.touched.name && formData.errors.name;
  const descriptionError =
    formData.touched.description && formData.errors.description;
  const ageError = formData.touched.age && formData.errors.age;
  const dateOfBirthError =
    formData.touched.dateOfBirth && formData.errors.dateOfBirth;
  const heightError = formData.touched.height && formData.errors.height;
  const widthError = formData.touched.width && formData.errors.width;
  const phoneNumberError =
    formData.touched.phoneNumber && formData.errors.phoneNumber;
  const addressError = formData.touched.address && formData.errors.address;
  const licenseError =
    formData.touched.driverLicenseNumber && formData.errors.driverLicenseNumber;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a subject</DialogTitle>
      <DialogContent>
        <List>
          <ListSubheader disableSticky>General</ListSubheader>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  id={"name"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.name}
                  onChange={formData.handleChange}
                  error={nameError}
                  helperText={nameError ? formData.errors.name : null}
                  label={"Name"}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={subjectType}
                    onChange={(e) => setSubjectType(e.target.value)}
                  >
                    {Object.values(constants.subject.type).map((type) => (
                      <MenuItem value={type.value}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </ListItem>
          <ListItem>
            <TextField
              fullWidth
              multiline
              id={"description"}
              variant={"filled"}
              onBlur={formData.onBlur}
              value={formData.values.description}
              onChange={formData.handleChange}
              error={descriptionError}
              helperText={descriptionError ? formData.errors.description : null}
              label={"Description"}
            />
          </ListItem>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  multiline
                  id={"age"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.age}
                  onChange={formData.handleChange}
                  error={ageError}
                  helperText={ageError ? formData.errors.age : null}
                  label={"Age"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  multiline
                  id={"dateOfBirth"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.dateOfBirth}
                  onChange={formData.handleChange}
                  error={dateOfBirthError}
                  helperText={
                    dateOfBirthError ? formData.errors.dateOfBirth : null
                  }
                  label={"Date of birth"}
                />
              </Grid>
            </Grid>
          </ListItem>
          <ListSubheader disableSticky>Physical attributes</ListSubheader>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Sex</InputLabel>
                  <Select
                    value={subjectSex}
                    onChange={(e) => setSubjectSex(e.target.value)}
                  >
                    {Object.values(constants.subject.sex).map((type) => (
                      <MenuItem value={type.value}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Race</InputLabel>
                  <Select
                    value={subjectRace}
                    onChange={(e) => setSubjectRace(e.target.value)}
                  >
                    {Object.values(constants.subject.race).map((type) => (
                      <MenuItem value={type.value}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Ethnicity</InputLabel>
                  <Select
                    value={subjectEthnicity}
                    onChange={(e) => setSubjectEthnicity(e.target.value)}
                  >
                    {Object.values(constants.subject.ethnicity).map((type) => (
                      <MenuItem value={type.value}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </ListItem>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Hair</InputLabel>
                  <Select
                    value={subjectHair}
                    onChange={(e) => setSubjectHair(e.target.value)}
                  >
                    {Object.values(constants.subject.hairColor).map((type) => (
                      <MenuItem value={type.value}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>Eyes</InputLabel>
                  <Select
                    value={subjectEyes}
                    onChange={(e) => setSubjectEyes(e.target.value)}
                  >
                    {Object.values(constants.subject.eyeColor).map((type) => (
                      <MenuItem value={type.value}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </ListItem>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id={"height"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.height}
                  onChange={formData.handleChange}
                  error={heightError}
                  helperText={heightError ? formData.errors.height : null}
                  label={"Height"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id={"weight"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.weight}
                  onChange={formData.handleChange}
                  error={widthError}
                  helperText={widthError ? formData.errors.weight : null}
                  label={"Weight"}
                />
              </Grid>
            </Grid>
          </ListItem>
          <ListSubheader disableSticky>Contact information</ListSubheader>
          <ListItem>
            <TextField
              fullWidth
              id={"phoneNumber"}
              variant={"filled"}
              onBlur={formData.onBlur}
              value={formData.values.phoneNumber}
              onChange={formData.handleChange}
              error={phoneNumberError}
              helperText={phoneNumberError ? formData.errors.phoneNumber : null}
              label={"Phone Number"}
            />
          </ListItem>
          <ListItem>
            <TextField
              fullWidth
              id={"address"}
              variant={"filled"}
              onBlur={formData.onBlur}
              value={formData.values.address}
              onChange={formData.handleChange}
              error={addressError}
              helperText={addressError ? formData.errors.waddress : null}
              label={"Address"}
            />
          </ListItem>
          <ListSubheader disableSticky>Driver's License</ListSubheader>
          <ListItem>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id={"driverLicenseNumber"}
                  variant={"filled"}
                  onBlur={formData.onBlur}
                  value={formData.values.driverLicenseNumber}
                  onChange={formData.handleChange}
                  error={licenseError}
                  helperText={
                    licenseError ? formData.errors.driverLicenseNumber : null
                  }
                  label={"Number"}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="filled" fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={subjectLicenseState}
                    onChange={(e) => setSubjectLicenseState(e.target.value)}
                  >
                    {Object.values(constants.states).map((type) => (
                      <MenuItem value={type.abbreviation}>{type.text}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={formData.submitForm} color="primary">
          Save
        </Button>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(() => ({
  loadingContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
  },
}));

export default MutateIncidentReportSubject;
